import '../public/welcome.css';
import '../public/app-nav.css';
import './home.css';
import './tax.css';
import './appShell.css';
import chatOutlet from './shell/outlets/chatOutlet.html?raw';
import faqOutlet from './shell/outlets/faqOutlet.html?raw';
import homeOutlet from './shell/outlets/homeOutlet.html?raw';
import taxOutlet from './shell/outlets/taxOutlet.html?raw';
import socialInsuranceOutlet from './shell/outlets/socialInsuranceOutlet.html?raw';
import basicInfoMenuHtml from './shell/basicInfoMenu.html?raw';
import { hasSettingsUnsavedChanges, initSettingsShell } from './settingsPage';
import { appConfirm } from './lib/appDialog';
import { initTaxShell } from './taxPage';
import { initSocialInsurancePage } from './socialInsurancePage';
import {
  mountAppNav,
  navKeyToDocumentTitle,
  pathnameToNavKey,
  setAppNavActive,
  type AppNavKey
} from './appNav';
import {
  applyBasicInfoToForm,
  BASIC_INFO_LOCAL_KEY,
  clearBasicInfoForm,
  hydrateBasicInfoFromLocal,
  loadBasicInfoForSession,
  loadBasicInfoFromLocal,
  persistBasicInfoFormToLocal,
  populateBasicInfoSelects,
  refreshBasicInfoDobDays
} from './basicInfoMenuApi';
import { principalToStableAuthorId, type PrincipalLike } from './lib/appUserId';

let chatCssLoaded = false;
const TAX_DISCLAIMER_ACCEPTED_KEY = 'tax_disclaimer_accepted_v2';
const SOCIAL_DISCLAIMER_ACCEPTED_KEY = 'social_disclaimer_accepted_v2';
const GUEST_ACK_ID = 'guest';
let shellAuthKnown = false;
let shellIsAuthed = false;
const TAX_DISCLAIMER_MESSAGE =
  'これは正確な税額計算を目的としていません。参考情報としてのみ利用してください。' +
  '\n一切の責任を負いません。' +
  '\n\n同意して「Tax」ページへ進みますか？';
const SOCIAL_DISCLAIMER_MESSAGE =
  'これは正確な社会保険料計算を目的としていません。参考情報としてのみ利用してください。' +
  '\n一切の責任を負いません。' +
  '\n\n同意して「Social」ページへ進みますか？';

/** 現在のタブ（BasicInfo の配置先に使用） */
let shellOutletKey: AppNavKey = 'home';

/**
 * 認証後シェル:
 * 左 .app-shell-sidebar … Nav ＋ BasicInfo（常時）
 * 右 .app-shell-main … Body（#app-outlet）
 */
function mountBasicInfoMenu(): void {
  const slot = document.getElementById('basic-info-menu-slot');
  if (!slot || slot.dataset.mounted === '1') return;
  slot.innerHTML = basicInfoMenuHtml.trim();
  populateBasicInfoSelects();
  // 認証待ちの前に localStorage から同期復元（空フォームで上書きしない）
  hydrateBasicInfoFromLocal();
  slot.dataset.mounted = '1';
}

let basicInfoPersistTimer: number | null = null;

function schedulePersistBasicInfoToLocal(): void {
  if (basicInfoPersistTimer !== null) {
    window.clearTimeout(basicInfoPersistTimer);
  }
  basicInfoPersistTimer = window.setTimeout(() => {
    basicInfoPersistTimer = null;
    persistBasicInfoFormToLocal();
  }, 0);
}

function initBasicInfoMenuActions(): void {
  const menu = document.getElementById('basic-info-menu');
  if (!menu || menu.dataset.actionsBound === '1') return;

  menu.querySelectorAll<HTMLSelectElement>('select.basic-info-select').forEach((sel) => {
    sel.addEventListener('change', () => {
      if (sel.id === 'basic-info-dob-y' || sel.id === 'basic-info-dob-m') {
        refreshBasicInfoDobDays();
      }
      schedulePersistBasicInfoToLocal();
    });
  });

  window.addEventListener('storage', (e) => {
    if (e.key !== BASIC_INFO_LOCAL_KEY) return;
    const data = loadBasicInfoFromLocal();
    if (data) applyBasicInfoToForm(data);
    else clearBasicInfoForm();
  });
  menu.dataset.actionsBound = '1';
}

function normalizePathname(pathname: string): string {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/chat') return '/chat.html';
  if (p === '/memo' || p === '/memo/index.html') return '/chat.html';
  if (p === '/community' || p === '/community.html') return '/chat.html';
  if (p === '/tax') return '/tax.html';
  if (p === '/social-insurance') return '/social-insurance.html';
  if (p === '/settings' || p === '/account' || p === '/account.html' || p === '/settings.html') {
    return '/chat.html';
  }
  return p;
}

function currentNormalizedPath(): string {
  return normalizePathname(window.location.pathname) + window.location.search;
}

function rolesIncludeAuthed(roles: string[] | undefined): boolean {
  if (!Array.isArray(roles)) return false;
  return roles.includes('authenticated') || roles.includes('administrator');
}

async function ensureShellAuthResolved(): Promise<boolean> {
  if (shellAuthKnown) return shellIsAuthed;
  try {
    const res = await fetch('/.auth/me', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
      shellIsAuthed = false;
    } else {
      const data = (await res.json().catch(() => null)) as
        | { clientPrincipal?: PrincipalLike[] | PrincipalLike }
        | null;
      const principal = Array.isArray(data?.clientPrincipal)
        ? data.clientPrincipal[0]
        : data?.clientPrincipal;
      const roles = Array.isArray((principal as { userRoles?: string[] } | null | undefined)?.userRoles)
        ? ((principal as { userRoles?: string[] }).userRoles as string[])
        : [];
      shellIsAuthed = Boolean(principal) && rolesIncludeAuthed(roles);
    }
  } catch {
    shellIsAuthed = false;
  }
  shellAuthKnown = true;
  return shellIsAuthed;
}

async function fetchCurrentDisclaimerUserId(): Promise<string> {
  try {
    const res = await fetch('/.auth/me', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return GUEST_ACK_ID;
    const data = (await res.json().catch(() => null)) as
      | { clientPrincipal?: PrincipalLike[] | PrincipalLike }
      | null;
    const principal = Array.isArray(data?.clientPrincipal)
      ? data.clientPrincipal[0]
      : data?.clientPrincipal;
    return principalToStableAuthorId(principal) || GUEST_ACK_ID;
  } catch {
    return GUEST_ACK_ID;
  }
}

async function ensureTaxDisclaimerAccepted(kind: 'tax' | 'socialInsurance'): Promise<boolean> {
  const storageKey = kind === 'tax' ? TAX_DISCLAIMER_ACCEPTED_KEY : SOCIAL_DISCLAIMER_ACCEPTED_KEY;
  const message = kind === 'tax' ? TAX_DISCLAIMER_MESSAGE : SOCIAL_DISCLAIMER_MESSAGE;
  const currentUserId = await fetchCurrentDisclaimerUserId();
  try {
    if (sessionStorage.getItem(storageKey) === currentUserId) return true;
  } catch {
    /* no-op */
  }
  const ok = await appConfirm(message, {
    title: kind === 'tax' ? 'Tax の注意' : 'Social の注意',
    okText: '同意して進む',
    cancelText: '戻る'
  });
  if (!ok) return false;
  try {
    sessionStorage.setItem(storageKey, currentUserId);
  } catch {
    /* no-op */
  }
  return true;
}

async function renderOutlet(key: AppNavKey): Promise<void> {
  const outlet = document.getElementById('app-outlet');
  if (!outlet) return;

  shellOutletKey = key;

  const chatPark = document.getElementById('chat-outlet-park');

  if (key !== 'chat') {
    const chatRoot = outlet.querySelector('.chat-app');
    if (chatRoot && chatPark) chatPark.appendChild(chatRoot);
  }

  outlet.innerHTML = '';

  if (key === 'home') {
    outlet.innerHTML = homeOutlet;
    return;
  }
  if (key === 'faq') {
    outlet.innerHTML = faqOutlet;
    return;
  }
  if (key === 'chat') {
    const parkedChat = chatPark?.querySelector('.chat-app');
    if (parkedChat && chatPark) {
      outlet.appendChild(parkedChat);
      const { refreshChatAvatarsAfterShellNavigation } = await import('./chat/chatApp');
      refreshChatAvatarsAfterShellNavigation();
      if (window.location.hash === '#chat-settings') {
        document.getElementById('chat-settings')?.scrollIntoView({ block: 'start' });
      }
      return;
    }
    if (!chatCssLoaded) {
      await import('./chat/chatScoped.css');
      chatCssLoaded = true;
    }
    outlet.innerHTML = chatOutlet;
    const { bootChatApp } = await import('./chat/chatApp');
    await bootChatApp();
    await initSettingsShell();
    if (window.location.hash === '#chat-settings') {
      document.getElementById('chat-settings')?.scrollIntoView({ block: 'start' });
    }
    return;
  }
  if (key === 'tax') {
    outlet.innerHTML = taxOutlet;
    initTaxShell(outlet);
    return;
  }
  if (key === 'socialInsurance') {
    outlet.innerHTML = socialInsuranceOutlet;
    initSocialInsurancePage(outlet);
  }
}

async function navigate(pathWithSearch: string, push: boolean): Promise<void> {
  const url = new URL(pathWithSearch, window.location.origin);
  const rawP = url.pathname.replace(/\/$/, '') || '/';
  const fromLegacySettings =
    rawP === '/settings' ||
    rawP === '/settings.html' ||
    rawP === '/account' ||
    rawP === '/account.html';
  const pathOnly = normalizePathname(url.pathname);
  const nextFull = pathOnly + url.search;
  const hash = url.hash || (fromLegacySettings ? '#chat-settings' : '');
  const targetKey = pathnameToNavKey(pathOnly);
  if (nextFull === currentNormalizedPath() && push) {
    if (targetKey === 'chat' && hash === '#chat-settings') {
      if (push) history.pushState(null, '', nextFull + hash);
      document.getElementById('chat-settings')?.scrollIntoView({ block: 'start' });
      return;
    }
    if (targetKey === 'chat') {
      window.dispatchEvent(new CustomEvent('chat-reset-to-list'));
    }
    return;
  }

  const key = targetKey;
  if (key === null) {
    window.location.assign(pathWithSearch);
    return;
  }
  if (shellOutletKey === 'chat' && key !== 'chat' && hasSettingsUnsavedChanges()) {
    const ok = await appConfirm('保存していない変更があります。このまま移動してもいいですか？', {
      title: '未保存の変更',
      okText: '移動する',
      cancelText: '残る'
    });
    if (!ok) return;
  }
  if ((key === 'tax' || key === 'socialInsurance') && !(await ensureTaxDisclaimerAccepted(key))) {
    return;
  }

  if (push) {
    history.pushState(null, '', nextFull + hash);
  }

  document.title = navKeyToDocumentTitle(key);
  setAppNavActive(key);
  await renderOutlet(key);
}

function isSpaInternalLink(pathname: string): boolean {
  return pathnameToNavKey(normalizePathname(pathname)) !== null;
}

function onDocumentClick(e: MouseEvent): void {
  if (e.defaultPrevented) return;
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const t = e.target as HTMLElement | null;
  const a = t?.closest('a');
  if (!a || a.target === '_blank') return;
  const href = a.getAttribute('href');
  if (!href || href.startsWith('#')) return;
  let u: URL;
  try {
    u = new URL(href, window.location.origin);
  } catch {
    return;
  }
  if (u.origin !== window.location.origin) return;
  if (!isSpaInternalLink(u.pathname)) return;
  e.preventDefault();
  void navigate(u.pathname + u.search + u.hash, true);
}

async function boot(): Promise<void> {
  const raw = window.location.pathname.replace(/\/$/, '') || '/';
  if (
    raw === '/account' ||
    raw === '/account.html' ||
    raw === '/settings' ||
    raw === '/settings.html'
  ) {
    history.replaceState(null, '', '/chat.html#chat-settings');
  }
  if (raw === '/community' || raw === '/community.html') {
    history.replaceState(null, '', '/chat.html' + window.location.search);
  }
  const pathOnly = normalizePathname(window.location.pathname);
  let key = pathnameToNavKey(pathOnly);
  if (key === null) {
    window.location.replace('/home.html');
    return;
  }
  if ((key === 'tax' || key === 'socialInsurance') && !(await ensureTaxDisclaimerAccepted(key))) {
    key = 'home';
    history.replaceState(null, '', '/home.html');
  }

  mountBasicInfoMenu();
  initBasicInfoMenuActions();
  // local が空のときだけ、認証後にサーバーから補完
  void ensureShellAuthResolved().then((isAuthed) =>
    loadBasicInfoForSession(isAuthed).then((data) => {
      if (!data) return;
      // hydrate 済みの local と同じなら DOM はそのまま
      const current = loadBasicInfoFromLocal();
      if (
        current &&
        current.year === data.year &&
        current.month === data.month &&
        current.dobY === data.dobY &&
        current.dobM === data.dobM &&
        current.dobD === data.dobD
      ) {
        return;
      }
      applyBasicInfoToForm(data);
    })
  );
  mountAppNav(key);
  document.title = navKeyToDocumentTitle(key);
  await renderOutlet(key);

  document.addEventListener('click', onDocumentClick);

  window.addEventListener('popstate', () => {
    const p = normalizePathname(window.location.pathname);
    void (async () => {
      let k = pathnameToNavKey(p);
      if (k === null) {
        window.location.reload();
        return;
      }
      if ((k === 'tax' || k === 'socialInsurance') && !(await ensureTaxDisclaimerAccepted(k))) {
        k = 'home';
        history.replaceState(null, '', '/home.html');
      }
      document.title = navKeyToDocumentTitle(k);
      setAppNavActive(k);
      await renderOutlet(k);
    })();
  });
}

void boot();
